import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import LeftChevron from '@material-ui/icons/ChevronLeft';
import SnackBar from '../SolvPatternLibrary/SnackBar';
import Grid from '@material-ui/core/Grid';
import { analyticsTrackEvent } from '../../core/analytics';
import { TOUCH_ENTER_MANUALLY, TOUCH_SKIP_BACK } from '../../constants';
import { colors } from '../../constants/colors';
import InsuranceForm from './InsuranceForm';
import InsuranceUpload from './MobileAddInsurance';
import InsuranceCardShape from '../../shapes/InsuranceCard';
import { fontFamily } from '../../constants/text';
import { Collapse } from '@material-ui/core';
import {
  ADD_INSURANCE_ORIGIN_USER_PROFILE,
  ADD_INSURANCE_ORIGIN_APP_BOOKING,
} from './util/routing';
import history from '../../core/history';

const StyledTitle = styled.div`
  color: ${colors.squid};
  font-family: ${fontFamily.bold};
  text-align: center;
  margin: 24px 24px ${props => (props.sc && props.sc.withoutSubtitle ? '15px' : '0')};
  font-size: 18px;
  letter-spacing: 0.62px;
  line-height: 28px;
`;

const SubTitle = styled.div`
  color: #5b6b7d;
  font-family: ${fontFamily.book};
  font-size: 14px;
  font-weight: 300;
  letter-spacing: -0.2px;
  line-height: 20px;
  text-align: center;
  margin-left: 24px;
  margin-right: 24px;
`;

const Anchor = styled(Grid)`
  width: 100vw;
  overflow: hidden;
`;

const gridAreaWithBack = css`
  grid-template-areas: 'back title blank' 'back title blank';
`;

const gridAreaOnlyTitle = css`
  grid-template-areas: 'title title title';
`;

const TopContainer = styled.div`
  display: grid;
  grid-template-columns: 20% 60% 20%;
  ${props => (props.sc.showBack ? gridAreaWithBack : gridAreaOnlyTitle)};
  align-items: center;
`;

const ChevronLeft = styled(LeftChevron)`
  color: ${colors.crunchBerry};
`;

const TitleContainer = styled.div`
  grid-area: title;
`;

const GoBackButton = styled(Button)`
  grid-area: back;
  color: ${colors.crunchBerry};
`;

const FormContainer = styled.div`
  display: inline-block;
  width: 100vw;
  vertical-align: top;
`;

const defaultState = {
  showForm: false,
  skipBack: false,
  submitSuccess: false,
};

class InsuranceController extends PureComponent {
  static propTypes = {
    handleSkip: PropTypes.func,
    hideForm: PropTypes.func,
    insuranceCard: InsuranceCardShape,
    clearCardData: PropTypes.func,
    addInsuranceFlow: PropTypes.string,
  };

  state = defaultState;

  componentDidMount = () => this.scanCompleted() && this.setState({ showForm: true });

  componentDidUpdate = ({ insuranceCard: { front, back } }) => {
    const {
      insuranceCard: { front: newFront, back: newBack },
    } = this.props;
    const { skipBack } = this.state;
    if (skipBack && !front.scanCompleted && newFront.scanCompleted) {
      this.setState({ ...defaultState, showForm: true });
      return;
    }

    if (
      !(front.scanCompleted && back.scanCompleted) &&
      (newFront.scanCompleted && newBack.scanCompleted)
    ) {
      this.setState({ ...defaultState, showForm: true });
    }
  };

  handleEnterManually = ({ from } = {}) => {
    const { addInsuranceFlow } = this.props;
    analyticsTrackEvent(TOUCH_ENTER_MANUALLY, { addInsuranceFlow, from });
    this.setState({ ...this.state, showForm: true });
  };

  handleSkipBack = () => {
    const {
      insuranceCard: { front },
      addInsuranceFlow,
    } = this.props;
    analyticsTrackEvent(TOUCH_SKIP_BACK, { addInsuranceFlow });
    if (front.scanCompleted) {
      this.setState({ skipBack: true, showForm: true });
      return;
    }

    this.setState({ skipBack: true });
  };

  scanStarted = () => {
    const {
      insuranceCard: { front, back },
    } = this.props;
    const { skipBack } = this.state;
    return Boolean((skipBack && front.scanStarted) || (front.scanStarted && back.scanStarted));
  };

  scanCompleted = () => {
    const {
      insuranceCard: { front, back },
    } = this.props;
    const { skipBack } = this.state;
    return Boolean(
      (skipBack && front.scanCompleted) || (front.scanCompleted && back.scanCompleted)
    );
  };

  isAddFromAccountFlow = () => this.props.addInsuranceFlow === ADD_INSURANCE_ORIGIN_USER_PROFILE;
  isAddFromAppBookingFlow = () => this.props.addInsuranceFlow === ADD_INSURANCE_ORIGIN_APP_BOOKING;

  shouldShowBack = () => this.isAddFromAccountFlow() || this.isAddFromAppBookingFlow();

  render = () => {
    const { handleSkip, ...childProps } = this.props;
    const { showForm, skipBack } = this.state;
    return (
      <Grid container justify="center">
        <Grid item xs={12}>
          <TopContainer sc={{ showBack: this.shouldShowBack() }}>
            {this.shouldShowBack() && (
              <GoBackButton variation={ButtonVariations.link} onClick={() => history.goBack()}>
                <ChevronLeft /> Back
              </GoBackButton>
            )}
            <TitleContainer>
              <Collapse in={!showForm} timeout={'auto'}>
                <StyledTitle sc={{ withoutSubtitle: this.isAddFromAccountFlow() }}>
                  {this.scanStarted() && 'Scanning your insurance'}
                  {!this.scanStarted() &&
                    !this.scanCompleted() &&
                    'Add photos of your insurance card'}
                </StyledTitle>
                {!this.isAddFromAccountFlow() && (
                  <SubTitle data-testid="subtitle">
                    We'll send your insurance to the clinic to verify
                  </SubTitle>
                )}
              </Collapse>
              <Collapse in={showForm} timeout={'auto'}>
                <StyledTitle sc={{ withoutSubtitle: true }}>
                  Complete your insurance details
                </StyledTitle>
              </Collapse>
            </TitleContainer>
          </TopContainer>
        </Grid>
        <Anchor item xs={12}>
          <Collapse in={!showForm} timeout={'auto'}>
            <FormContainer>
              <InsuranceUpload
                handleEnterManually={this.handleEnterManually}
                handleSkipBack={this.handleSkipBack}
                skipBack={skipBack}
                handleSkipInsurance={handleSkip}
                {...childProps}
              />
            </FormContainer>
          </Collapse>
          <Collapse in={showForm} timeout={'auto'}>
            <FormContainer>
              <InsuranceForm {...this.props} />
            </FormContainer>
          </Collapse>
        </Anchor>
        <SnackBar open={this.scanStarted()} autoHideDuration={3000}>
          Scanning and saving photos...
        </SnackBar>
        <SnackBar open={this.scanCompleted()} autoHideDuration={3000}>
          Insurance card photos saved
        </SnackBar>
        <SnackBar
          open={this.state.submitSuccess}
          autoHideDuration={3000}
          data-testid="submit-success"
        >
          {this.isAddFromAccountFlow()
            ? 'Adding insurance to your account...'
            : 'Sending your insurance to the clinic...'}
        </SnackBar>
      </Grid>
    );
  };
}

export default InsuranceController;
